import { forwardRef } from "react";

import { cn } from "../../lib/utils.ts";

/**
 * The mark's shapes, named once because the sheen redraws most of them.
 *
 * The body and the two heads carry their sheen as stacked tinted copies, so
 * each of those paths would otherwise appear three times over and a correction
 * would have to be made in three places consistently.
 */
const SHOULDER =
  "M22 20H34C37.3137 20 40 22.6863 40 26V36C40 39.3137 37.3137 42 34 42C30.6863 42 28 39.3137 28 36V26C28 22.6863 25.3137 20 22 20Z";
const BODY =
  "M8 24C8 20.6863 10.6863 18 14 18H22C25.3137 18 28 20.6863 28 24V36C28 39.3137 30.6863 42 34 42L18 42C12.4772 42 8 37.5228 8 32V24Z";
const SMALL_HEAD =
  "M33 18C35.7614 18 38 15.7614 38 13C38 10.2386 35.7614 8 33 8C30.2386 8 28 10.2386 28 13C28 15.7614 30.2386 18 33 18Z";
const LARGE_HEAD =
  "M18 16C21.3137 16 24 13.3137 24 10C24 6.68629 21.3137 4 18 4C14.6863 4 12 6.68629 12 10C12 13.3137 14.6863 16 18 16Z";
const T_GLYPH = "M15.4792 28.1054H13.0321V35.5714H10.9673V28.1054H8.52014V26.4286H15.4792V28.1054Z";

/**
 * Microsoft Teams brand mark, inline SVG so we do not depend on a hosted asset.
 *
 * Official FY26 artwork. Colour lives in the gradients rather than in Tailwind
 * `text-*` classes so the mark renders correctly whatever the parent text colour
 * is, matching `SlackIcon` / `WhatsAppIcon` / `OutlookIcon` in this folder.
 *
 * The viewBox is tightened to the artwork's own bounds (x 4..40, y 4..42), padded
 * by one unit a side to stay square, rather than the exported 48-square.
 * Microsoft's export pads the mark; Slack's and WhatsApp's run edge to edge, so
 * at the same pixel size this one drew about a quarter smaller than the marks it
 * sits beside. Coordinates are rounded to the exported integers; the shapes and
 * their bounds are unchanged.
 *
 * Gradient ids carry a `teams-` prefix for the same reason `OutlookIcon`'s do:
 * ids are global to the document, so an unprefixed `paint0_radial` would answer
 * another icon's `url(#...)` as readily as its own.
 *
 * Forwards its ref and spreads the rest of its props, which Radix depends on:
 * `TooltipTrigger asChild` clones its child with the trigger's handlers, its
 * `aria-describedby` and the Popper anchor ref, and a component taking only
 * `className` drops all three silently.
 */
export const TeamsIcon = forwardRef<SVGSVGElement, React.SVGProps<SVGSVGElement>>(
  ({ className, ...props }, ref) => (
    <svg
      ref={ref}
      viewBox="3 4 38 38"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn(className)}
      aria-label="Microsoft Teams"
      role="img"
      {...props}
    >
      <path d={SHOULDER} fill="url(#teams-shoulder)" />
      <path d={BODY} fill="url(#teams-body)" />
      <path d={BODY} fill="url(#teams-body-fade)" fillOpacity="0.7" />
      <path d={BODY} fill="url(#teams-body-sheen)" fillOpacity="0.7" />
      <path d={SMALL_HEAD} fill="url(#teams-small-head)" />
      <path d={SMALL_HEAD} fill="url(#teams-small-head-sheen)" fillOpacity="0.46" />
      <path d={SMALL_HEAD} fill="url(#teams-small-head-glow)" fillOpacity="0.4" />
      <path d={LARGE_HEAD} fill="url(#teams-large-head)" />
      <path d={LARGE_HEAD} fill="url(#teams-large-head-sheen)" fillOpacity="0.6" />
      <path d={LARGE_HEAD} fill="url(#teams-large-head-glow)" fillOpacity="0.5" />
      <rect x="4" y="23" width="16" height="16" rx="3.25" fill="url(#teams-tile)" />
      <rect
        x="4"
        y="23"
        width="16"
        height="16"
        rx="3.25"
        fill="url(#teams-tile-sheen)"
        fillOpacity="0.7"
      />
      <path d={T_GLYPH} fill="white" />
      <defs>
        <radialGradient
          id="teams-shoulder"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(39.7967 22.1739) scale(13.4784 33.2694)"
        >
          <stop stopColor="#A98AFF" />
          <stop offset="0.14" stopColor="#8C75FF" />
          <stop offset="0.565" stopColor="#5F50E2" />
          <stop offset="0.9" stopColor="#3C2CB8" />
        </radialGradient>
        <radialGradient
          id="teams-body"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(8.81238 16.4) rotate(68.1539) scale(32.752 33.1231)"
        >
          <stop stopColor="#85C2FF" />
          <stop offset="0.69" stopColor="#7588FF" />
          <stop offset="1" stopColor="#6459FE" />
        </radialGradient>
        <linearGradient
          id="teams-body-fade"
          x1="20.5936"
          y1="18"
          x2="20.5936"
          y2="42"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.801159" stopColor="#6864F6" stopOpacity="0" />
          <stop offset="1" stopColor="#5149DE" />
        </linearGradient>
        <radialGradient
          id="teams-body-sheen"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(27.4999 17.2) rotate(113.326) scale(19.2186 15.4273)"
        >
          <stop stopColor="#BD96FF" />
          <stop offset="0.686685" stopColor="#BD96FF" stopOpacity="0" />
        </radialGradient>
        <radialGradient
          id="teams-small-head"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(32.9999 11.5714) rotate(-90) scale(10 12.6216)"
        >
          <stop offset="0.268201" stopColor="#6868F7" />
          <stop offset="1" stopColor="#3923B1" />
        </radialGradient>
        <radialGradient
          id="teams-small-head-sheen"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(28.8675 10.544) rotate(40.0516) scale(7.14629 10.3363)"
        >
          <stop offset="0.270711" stopColor="#A1D3FF" />
          <stop offset="0.813393" stopColor="#A1D3FF" stopOpacity="0" />
        </radialGradient>
        <radialGradient
          id="teams-small-head-glow"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(36.9821 10.3686) rotate(-41.6581) scale(8.51275 20.8824)"
        >
          <stop stopColor="#E3ACFD" />
          <stop offset="0.816041" stopColor="#9FA2FF" stopOpacity="0" />
        </radialGradient>
        <radialGradient
          id="teams-large-head"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(17.9999 8.28571) rotate(-90) scale(12 15.146)"
        >
          <stop offset="0.268201" stopColor="#8282FF" />
          <stop offset="1" stopColor="#3923B1" />
        </radialGradient>
        <radialGradient
          id="teams-large-head-sheen"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(13.0411 7.05284) rotate(40.0516) scale(8.57554 12.4035)"
        >
          <stop offset="0.270711" stopColor="#A1D3FF" />
          <stop offset="0.813393" stopColor="#A1D3FF" stopOpacity="0" />
        </radialGradient>
        <radialGradient
          id="teams-large-head-glow"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(22.7786 6.8423) rotate(-41.6581) scale(10.2153 25.0589)"
        >
          <stop stopColor="#E3ACFD" />
          <stop offset="0.816041" stopColor="#9FA2FF" stopOpacity="0" />
        </radialGradient>
        <radialGradient
          id="teams-tile"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(4 23) rotate(45) scale(22.6274)"
        >
          <stop offset="0.046875" stopColor="#688EFF" />
          <stop offset="0.946875" stopColor="#230F94" />
        </radialGradient>
        <radialGradient
          id="teams-tile-sheen"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(12 32.6) rotate(90) scale(11.2 13.0702)"
        >
          <stop offset="0.570647" stopColor="#6965F6" stopOpacity="0" />
          <stop offset="1" stopColor="#8F8FFF" />
        </radialGradient>
      </defs>
    </svg>
  ),
);
TeamsIcon.displayName = "TeamsIcon";
